<template>
  <div id="position-market-price">
    <span v-if="!showPositionMarketPrice">-</span>
    <div v-else-if="position.customMarket && position.customMarket.active">
      <currency
        v-if="customMarketPrice"
        neverHide
        :value="customMarketPrice"
        :currency="position.currency"
      />
      <span v-else></span>
    </div>
    <div v-else-if="position.type === 'cash' && position.ticker === position.currency">
      <currency v-if="eodPrice" neverHide :value="eodPrice" :currency="position.currency" />
      <span v-else></span>
    </div>
    <div v-else>
      <v-tooltip top max-width="300">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" class="items-flex" v-on="on">
            <div v-if="showIntraDay">
              <currency
                neverHide
                :value="intraDayPrice"
                :currency="position.currency"
                :decimals="intraDayPrice < 1 && position.type === 'crypto' ? 4 : undefined"
              />
            </div>
            <div v-else>
              <currency
                v-if="eodPrice"
                neverHide
                :value="eodPrice"
                :currency="position.currency"
                :decimals="eodPrice < 1 && position.type === 'crypto' ? 4 : undefined"
              />
              <span v-else></span>
            </div>
            <v-avatar size="7" :color="avatarColor" class="ml-1" />
          </div>
        </template>
        <span>
          <template v-if="eodStatus === 'errored'">
            {{ $t(`views.common.positionMarketPrice.errored`) }}
          </template>
          <template v-else-if="eodStatus === 'pending'">
            {{ $t(`views.common.positionMarketPrice.toBeUpdated`) }}
          </template>
          <template v-else>
            <template v-if="showIntraDay">
              {{ intraDayDateFormatted }}
            </template>
            <template v-else>
              {{ $t(`views.common.positionMarketPrice.eodDate`) }}
              {{ eodDateFormatted.toLowerCase() }}
            </template>
          </template>
        </span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import { getDateToFormat } from '@/lib/utils/date'
import Currency from '@/views/components/common/Currency.vue'
import { positionNotMarketPrice } from '@/lib/positions/positions'

export default {
  components: {
    Currency,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    position: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState(['language']),
    showPositionMarketPrice() {
      if (get(this.position, 'customMarket.active', false)) return true
      if (positionNotMarketPrice(this.position)) return false
      if (this.type === 'position' && this.position.positionNumber === 0) return false
      return true
    },
    eodPrice() {
      return get(this.position, 'market.eod.close', null)
    },
    eodDate() {
      const eodDate = get(this.position, 'market.eod.date', null)
      return DateTime.fromISO(eodDate)
    },
    eodDateFormatted() {
      if (!this.eodDate) return null
      return getDateToFormat({ date: this.eodDate, format: 'DD' })
    },
    intraDayStatus() {
      return get(this.position, 'market.intraDay.status', 'pending')
    },
    intraDayPrice() {
      return get(this.position, 'market.intraDay.close', null)
    },
    intraDayDate() {
      const intraDayDate = get(this.position, 'market.intraDay.date', null)
      return DateTime.fromISO(intraDayDate)
    },
    intraDayDateFormatted() {
      if (!this.intraDayDate) return null
      let formatted = this.$t(`views.common.positionMarketPrice.updated`)
      formatted += this.intraDayDate.toRelativeCalendar({ unit: 'days', locale: this.language })
      formatted += this.$t(`views.common.positionMarketPrice.at`)
      return formatted + getDateToFormat({ date: this.intraDayDate, format: 'T' })
    },
    showIntraDay() {
      return this.intraDayPrice && this.intraDayStatus === 'updated'
    },
    eodStatus() {
      return get(this.position, 'market.eod.status', 'pending')
    },
    avatarColor() {
      if (this.eodStatus === 'errored') return 'error'
      else if (this.eodStatus === 'pending') return 'grey'
      else return this.showIntraDay ? 'primary' : 'secondary'
    },

    customMarketPrice() {
      return get(this.position, 'customMarket.eod.close', null)
    },
  },
}
</script>

<style lang="scss">
#position-market-price {
  .items-flex {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
